import type { ComponentProps, JSX, ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import { Form, FormGroup, Help, FormRow } from '@/components/Form'

import { SearchResultItem } from '@/components/inputs/SearchResultItem'
import { Button } from '@/components/Button'
import styles from './RemoveClientsAsNonAdminDialog.module.css'
import { ErrorBlock } from '@/components/ErrorBlock'

export interface RemoveClientsAsNonAdminDialogProps {
  showing: boolean
  collaboratorSelect: ReactNode
  collaboratorError?: string
  isRemovePending: boolean
  isRemoveRejected: boolean
  removeError: unknown
  isDisabled: boolean
  onClose: () => unknown
  onRemove: () => unknown
  submitButtonTestId?: string
}

export function RemoveClientsAsNonAdminDialog({
  showing,
  collaboratorSelect,
  collaboratorError,
  isRemovePending,
  isRemoveRejected,
  removeError,
  isDisabled,
  onClose,
  onRemove,
  submitButtonTestId,
}: RemoveClientsAsNonAdminDialogProps): JSX.Element {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isRemovePending ? undefined : onClose}
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <div className={styles.contentBlock}>
          <ModalSheetContentTitle compact align="left">
            <span className={styles.title}>Remove a supporting client</span>
          </ModalSheetContentTitle>

          <ModalSheetContentDescription compact align="left">
            <span className={styles.description}>
              The user will no longer be a collaborator on the job.
            </span>
          </ModalSheetContentDescription>
        </div>

        <ModalSheetContentBlock>
          <Form>
            <FormRow className={styles.row}>
              <FormGroup
                label="Collaborator"
                className={styles.selectCollaboratorContainer}
              >
                {collaboratorSelect}
                <Help error={collaboratorError} />
              </FormGroup>
            </FormRow>
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>

      {isRemoveRejected && <ErrorBlock tackOn="bottom" error={removeError} />}

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            isDisabled={isRemovePending}
            variant="default"
            onPress={onClose}
          />
          <Button
            label="Remove"
            variant="destructive"
            isPending={isRemovePending}
            testId={submitButtonTestId}
            onPress={onRemove}
            isDisabled={isDisabled}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export function RemoveClientSearchItem(
  props: ComponentProps<typeof SearchResultItem>,
) {
  return <SearchResultItem className={styles.searchResultItem} {...props} />
}
